import React, { useRef, useState, useEffect, memo } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import Loading from 'components/utility/Loading';
import { Platform } from 'react-native';
import {
  Stack,
  SizableText,
  View,
  ScrollView,
  XStack,
  YStack,
  useTheme,
  useThemeName,
} from 'tamagui';
import { Flex, Center } from 'components/base/General';
import { Pressable } from 'react-native';
import {
  TouchableOpacity,
  CharHighlight,
  ColorButton,
  StarButton,
} from 'components/utility/Buttons';
import TonedChars from 'components/utility/TonedText';
import { MainStyles } from 'resources/styles/MainStyles';
import { LinearGradient } from 'tamagui/linear-gradient';
import ListCard from 'components/main/ListCard';
import { toggleLogin } from 'reducers/slices/loginstatus';
import { togglePayment } from 'reducers/slices/paymentstatus';
import { TrashCan, Floppy, Cross } from 'components/utility/General';
import { wait } from 'utils/misc';
import InAppReview from 'react-native-in-app-review';
import { getRandomIntInclusive } from 'utils/misc';
import { currentCharSave } from 'reducers/slices/currentchar';
import { subCheck } from 'controllers/ArticleController';
import { useRouter } from 'solito/router';
import Controls from 'components/utility/Controls';
import ArticleControls from 'components/main/ArticleControls';
import { DetailHeader } from 'app/components/base/Detail';
import {
  useIsArticleReadQuery,
  useCreateOrUpdateReadStatusMutation,
  useDeleteReadStatusMutation,
  useGetUserWordsQuery,
  useGetUserSentencesQuery,
  useGetRuleMappingsQuery,
} from 'utils/RTKGraphQL';
import Breadcrumb from 'components/utility/Breadcrumb';
import {
  setDictCollapse,
  setSentenceCollapse,
  setRulesCollapse,
} from 'reducers/slices/collapsesettings';
import { Collapsible } from 'app/components/base/General';
import { useSentenceOperations, useWordOperations } from 'utils/hooks';

const DetailChinese = (props: any) => {
  const colorMode = useThemeName();
  const dispatch = useDispatch();
  // Updated to use languagesettings slice
  const traditional = useSelector(
    (state: any) => state.languagesettings.traditional
  );
  const showPinyin = useSelector(
    (state: any) => state.languagesettings.showPinyin
  );
  const pinyinSize = useSelector((state: any) => state.uisettings.pinyinSize);
  const charSize = useSelector((state: any) => state.uisettings.charSize);

  const highlightSentenceWeb = (sentenceIndex: number) => {
    ReactDOM.findDOMNode(
      props.sentenceRefs.current[sentenceIndex]
    ).childNodes[0].style['background-color'] =
      colorMode === 'light' ? '#e5e5e5' : '#27272a';
  };

  const highlightSentenceMobile = (sentenceIndex: number) => {
    props.sentenceRefs.current[sentenceIndex].setNativeProps({
      style: { backgroundColor: colorMode === 'light' ? '#e5e5e5' : '#27272a' },
    });
  };

  const showSentenceRules = () => {
    props.rules.current = props.sentence.rule;
  };

  const showSentenceTranslation = () => {
    props.selectedSentence.current = props.sentence;
  };

  const clickSentenceWeb = () => {
    if (props.previousSentenceIndex.current !== null) {
      props.clearSentenceRef();
    }
    highlightSentenceWeb(props.sentenceIndex);

    showSentenceRules();
    showSentenceTranslation();
  };
  const clickSentenceMobile = () => {
    if (props.previousSentenceIndex.current !== null) {
      props.clearSentenceRef();
    }
    highlightSentenceMobile(props.sentenceIndex);
    showSentenceRules();
    showSentenceTranslation();
  };

  const clickSentence = (sentenceIndex: number) => {
    if (Platform.OS === 'web') {
      clickSentenceWeb(sentenceIndex);
    } else if (Platform.OS !== 'web') {
      clickSentenceMobile(sentenceIndex);
    }
    props.previousSentenceIndex.current = sentenceIndex;
  };

  const clickText = async (index: number, sentenceIndex: number) => {
    openCharOverlay(index, sentenceIndex);
  };

  const openCharOverlay = (index: number, sentenceIndex: number) => {
    dispatch(
      currentCharSave(props.sentences[sentenceIndex].json.results[index])
    );
  };

  useEffect(() => {
    props.previousIndex.current = props.index;
    props.previousSentenceWordIndex.current = props.sentenceIndex;
  }, [props.currentChar]);

  let contentMode: 'simplified' | 'traditional';
  if (traditional !== true) {
    contentMode = 'simplified';
  } else if (traditional === true) {
    contentMode = 'traditional';
  }

  return (
    <CharHighlight
      style={{ alignItems: 'center' }}
      delayPressIn={0}
      onPress={() => {
        clickText(props.index, props.sentenceIndex);
        clickSentence(props.sentenceIndex);
      }}
      activeOpacity={0.2}
    >
      <Stack my={20}>
        <Stack alignItems="center" height={20}>
          <TonedChars
            char_select={() => {}}
            disableTouch={true}
            fontSize={4 + pinyinSize}
            char_type={'pinyin'}
            word={showPinyin ? props.content.to_dictionary : ' '}
          />
        </Stack>
        <Stack>
          <TonedChars
            char_select={() => {}}
            disableTouch={true}
            fontSize={8 + charSize * 2}
            char_type={contentMode}
            word={props.content.to_dictionary}
            charSpace={true}
          />
        </Stack>
      </Stack>
    </CharHighlight>
  );
};

const DetailLatin = (props: any) => {
  const theme = useTheme();
  // Updated to use uisettings slice
  const pinyinSize = useSelector((state: any) => state.uisettings.pinyinSize);
  const charSize = useSelector((state: any) => state.uisettings.charSize);

  const lineHeightCalc = () => {
    let baseLH = 8 + charSize * 2;
    if (Platform.OS === 'ios') {
      baseLH = baseLH * 1.15;
    }
    return baseLH;
  };

  return (
    <Stack my={20}>
      <Stack height={23}>
        <SizableText
          color={theme.mainColor.get()}
          style={{
            fontSize: 4 + pinyinSize,
            textAlign: 'center',
            userSelect: Platform.OS === 'web' ? 'none' : undefined,
          }}
        >
          {'\u200B'}
        </SizableText>
      </Stack>
      <Stack justifyContent="center" alignSelf="center">
        <SizableText
          color={theme.mainColor.get()}
          style={{
            lineHeight: lineHeightCalc(),
            fontSize: 8 + charSize * 2,
            textAlign: 'center',
            textAlignVertical: 'bottom',
            userSelect: Platform.OS === 'web' ? 'none' : undefined,
          }}
        >
          {props.content.characters}
        </SizableText>
      </Stack>
    </Stack>
  );
};

const DetailBody = (props: any) => {
  if (!props.sentence.json) {
    return <Loading />;
  }

  return (
    <Flex flexShrink={1}>
      <Flex
        mx={24}
        $md={{
          mx: 40,
        }}
        justifyContent="center"
        alignSelf="center"
        ref={(el) => (props.sentenceRefs.current[props.sentenceIndex] = el)}
      >
        <XStack flexWrap="wrap">
          {props.sentence.json.results.map((content: any, index: number) => (
            <Stack
              key={index}
              ref={(el) => {
                if (
                  typeof props.textRefs.current[props.sentenceIndex] ===
                  'undefined'
                ) {
                  props.textRefs.current[props.sentenceIndex] = [];
                }
                props.textRefs.current[props.sentenceIndex][index] = el;
              }}
            >
              {content.chinese ? (
                <DetailChinese
                  content={content}
                  {...props}
                  index={index}
                  sentenceIndex={props.sentenceIndex}
                />
              ) : (
                <DetailLatin content={content} {...props} />
              )}
            </Stack>
          ))}
        </XStack>
      </Flex>
    </Flex>
  );
};

const InactivateSentenceWrapper = (props: any) => {
  const { inactivateSentence } = useSentenceOperations();

  const _inactivateSentence = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await inactivateSentence(props.selectedSentence.current);
  };

  return (
    <Flex flexShrink={1}>
      <TouchableOpacity onPress={_inactivateSentence}>
        <TrashCan size={props.size ? props.size : 20} darkColor="white" />
      </TouchableOpacity>
    </Flex>
  );
};

const SaveSentenceWrapper = (props: any) => {
  const { saveSentence } = useSentenceOperations();

  const _saveSentence = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await saveSentence(props.selectedSentence.current);
  };

  return (
    <Flex flexShrink={1}>
      <TouchableOpacity onPress={_saveSentence}>
        <Floppy size={props.size ? props.size : 20} darkColor="white" />
      </TouchableOpacity>
    </Flex>
  );
};

const InactivateWordWrapper = (props: any) => {
  const { inactivateWord } = useWordOperations();

  const handleInactivate = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await inactivateWord(props.currentChar);
    if (props.refetchUserWords) {
      try {
        await props.refetchUserWords();
      } catch (error) {
        console.error('Failed to refetch user words:', error);
      }
    }
  };

  return (
    <Flex flexShrink={1}>
      <TouchableOpacity onPress={handleInactivate}>
        <TrashCan size={props.size ? props.size : 20} darkColor="white" />
      </TouchableOpacity>
    </Flex>
  );
};

const SaveWordWrapper = (props: any) => {
  const { saveWord } = useWordOperations();

  const handleSave = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await saveWord(props.currentChar);
    if (props.refetchUserWords) {
      try {
        await props.refetchUserWords();
      } catch (error) {
        console.error('Failed to refetch user words:', error);
      }
    }
  };

  return (
    <Flex flexShrink={1}>
      <TouchableOpacity onPress={handleSave}>
        <Floppy size={props.size ? props.size : 20} darkColor="white" />
      </TouchableOpacity>
    </Flex>
  );
};

const CloseCharacterStackWrapper = (props: any) => {
  const dispatch = useDispatch();
  const closeCharStack = () => {
    props.clearTextRef();
    props.clearSentenceRef();
    dispatch(currentCharSave(null));
  };
  return (
    <Flex flexShrink={1}>
      <TouchableOpacity onPress={closeCharStack}>
        <Cross size={24} />
      </TouchableOpacity>
    </Flex>
  );
};

const CharOverlay = (props: any) => {
  const theme = useTheme();
  const currentChar = useSelector((state: any) => state.currentchar);
  const { controls } = useSelector((state: any) => state);

  if (currentChar === null) {
    return null;
  }

  return (
    <YStack
      bg={theme.sidebarGray.get()}
      pb={8}
      width={'100%'}
      pt={50}
      top={0}
      left={0}
      right={0}
      shadow="9"
      zIndex={1}
      borderBottomRightRadius={15}
      borderBottomLeftRadius={15}
      position={Platform.OS !== 'web' ? 'absolute' : 'sticky'}
    >
      <XStack justifyContent="space-between" mx={15}>
        <Stack />
        <CloseCharacterStackWrapper
          currentChar={currentChar}
          clearTextRef={props.clearTextRef}
          clearSentenceRef={props.clearSentenceRef}
        />
      </XStack>
      <YStack px={10} $md={{ px: 20 }} gap={8}>
        <CharacterDisplay {...props} currentChar={currentChar} />
        <DetailTranslation {...props} />
        <DetailRules {...props} />
      </YStack>
    </YStack>
  );
};

const CharacterDisplay = memo((props: any) => {
  const theme = useTheme();
  const dictCollapse = useSelector(
    (state: any) => state.collapsesettings.dictCollapse
  );
  // Updated to use languagesettings slice
  const traditional = useSelector(
    (state: any) => state.languagesettings.traditional
  );
  const char_type = traditional ? 'traditional' : 'simplified';
  const opp_char_type = traditional ? 'simplified' : 'traditional';
  const dispatch = useDispatch();
  const profile = useSelector((state: any) => state.profile);

  const {
    data: words,
    isLoading,
    error,
    refetch: refetchUserWords,
  } = useGetUserWordsQuery(
    { userId: profile?.id, active: true },
    { skip: !profile?.id }
  );

  const wordIds = words?.map((word: any) => word.fromDictionary[0].id) || [];

  const handleToggle = () => {
    dispatch(setDictCollapse(!dictCollapse));
  };

  return (
    <Pressable onPress={handleToggle}>
      <YStack
        bg={theme.charDis}
        borderTopRightRadius={5}
        borderBottomRightRadius={5}
        borderTopLeftRadius={10}
        borderBottomLeftRadius={10}
      >
        <XStack>
          <Stack
            bg={'$blue400'}
            height="100%"
            width="4%"
            borderTopLeftRadius={10}
            borderBottomLeftRadius={10}
          />
          <YStack
            flex={1}
            bg={theme.charDis}
            borderTopRightRadius={5}
            borderBottomRightRadius={5}
            px={8}
            pt={8}
            pb={8}
          >
            <XStack>
              <Stack>
                <SizableText fontWeight="bold" color={theme.mainColor.get()}>
                  Dictionary Definition
                </SizableText>
              </Stack>

              <Flex flexGrow={1} alignItems="flex-end">
                {wordIds.includes(props.currentChar.id) ? (
                  <InactivateWordWrapper
                    {...props}
                    refetchUserWords={refetchUserWords}
                  />
                ) : (
                  <SaveWordWrapper
                    {...props}
                    refetchUserWords={refetchUserWords}
                  />
                )}
              </Flex>
            </XStack>
            <Collapsible collapsed={dictCollapse}>
              <YStack space="$2">
                <XStack justifyContent="space-between" alignItems="center">
                  <TonedChars
                    disableTouch={true}
                    fontSize={16}
                    char_type={'pinyin'}
                    word={props.currentChar.to_dictionary}
                  />
                  <SizableText
                    fontSize="$lg"
                    fontWeight="bold"
                    style={[MainStyles['TextHSK' + props.currentChar.skill]]}
                  >
                    {props.currentChar.skill < 10
                      ? 'HSK' + props.currentChar.skill
                      : ''}
                  </SizableText>
                </XStack>
                <XStack justifyContent="center" alignItems="center">
                  <TonedChars
                    disableTouch={true}
                    fontSize={30}
                    char_type={char_type}
                    word={props.currentChar.to_dictionary}
                    dashDisabled={true}
                  />
                </XStack>
                <XStack justifyContent="space-between" alignItems="center">
                  <TonedChars
                    disableTouch={true}
                    fontSize={16}
                    char_type={opp_char_type}
                    word={props.currentChar.to_dictionary}
                    dashDisabled={true}
                  />
                </XStack>
                <XStack>
                  <SizableText color={theme.mainColor.get()} fontSize="$sm">
                    {props.currentChar.translation}
                  </SizableText>
                </XStack>
              </YStack>
            </Collapsible>
          </YStack>
        </XStack>
      </YStack>
    </Pressable>
  );
});

const DetailTranslation = (props: any) => {
  const theme = useTheme();
  const totalCollapse = useSelector((state: any) => state.collapsesettings);
  const sentenceCollapse = useSelector(
    (state: any) => state.collapsesettings.sentenceCollapse
  );
  const profile = useSelector((state: any) => state.profile);
  const dispatch = useDispatch();

  const {
    data: userSentences,
    isLoading: isUserSentencesLoading,
    error: userSentencesError,
    refetch: refetchUserSentences,
  } = useGetUserSentencesQuery(
    { userId: profile?.id, active: true },
    { skip: !profile?.id }
  );

  const isSentenceSaved = userSentences?.some(
    (sentence: any) =>
      sentence.sentenceId === props.selectedSentence.current?.id
  );

  const handleToggle = () => {
    dispatch(setSentenceCollapse(!sentenceCollapse));
  };

  return (
    <Pressable onPress={handleToggle}>
      <YStack
        bg={theme.charTrans}
        borderTopRightRadius={5}
        borderBottomRightRadius={5}
        borderTopLeftRadius={10}
        borderBottomLeftRadius={10}
      >
        <XStack>
          <Stack
            bg={theme.charTransInner}
            height="100%"
            width="4%"
            borderTopLeftRadius={10}
            borderBottomLeftRadius={10}
          />
          <YStack
            flex={1}
            bg={theme.charTrans}
            borderTopRightRadius={5}
            borderBottomRightRadius={5}
            px={8}
            pt={8}
            pb={8}
          >
            <XStack>
              <Stack>
                <SizableText fontWeight="bold" color={theme.mainColor.get()}>
                  Sentence Translation
                </SizableText>
              </Stack>
              {props.selectedSentence.current !== null ? (
                <Flex flexGrow={1} alignItems="flex-end">
                  {isSentenceSaved ? (
                    <InactivateSentenceWrapper
                      {...props}
                      refetchUserSentences={refetchUserSentences}
                    />
                  ) : (
                    <SaveSentenceWrapper
                      {...props}
                      refetchUserSentences={refetchUserSentences}
                    />
                  )}
                </Flex>
              ) : null}
            </XStack>
            <Collapsible
              collapsed={sentenceCollapse}
              key={props.selectedSentence.current?.translation[0]?.translation}
            >
              {props.selectedSentence.current !== null ? (
                <YStack flex={1}>
                  <SizableText
                    color={theme.mainColor.get()}
                    flexWrap="wrap"
                    flex={1}
                    style={{
                      wordWrap:
                        Platform.OS === 'web' ? 'break-word' : undefined,
                      overflowWrap:
                        Platform.OS === 'web' ? 'break-word' : undefined,
                    }}
                  >
                    {props.selectedSentence.current.translation[0].translation}
                  </SizableText>
                </YStack>
              ) : null}
            </Collapsible>
          </YStack>
        </XStack>
      </YStack>
    </Pressable>
  );
};

const DetailRules = (props: any) => {
  const theme = useTheme();
  const rulesCollapse = useSelector(
    (state: any) => state.collapsesettings.rulesCollapse
  );
  const dispatch = useDispatch();

  const handleToggle = () => {
    dispatch(setRulesCollapse(!rulesCollapse));
  };

  if (props.rules.current.length === 0) {
    return <Stack />;
  }

  return (
    <Pressable onPress={handleToggle}>
      <YStack
        bg={theme.charRules}
        borderTopRightRadius={5}
        borderBottomRightRadius={5}
        borderTopLeftRadius={10}
        borderBottomLeftRadius={10}
      >
        <XStack>
          <Stack
            bg={theme.charRulesInner}
            height="100%"
            width="4%"
            borderTopLeftRadius={10}
            borderBottomLeftRadius={10}
          />
          <YStack
            flex={1}
            bg={theme.charRules}
            borderTopRightRadius={5}
            borderBottomRightRadius={5}
            px={8}
            pt={8}
            pb={8}
          >
            <Stack>
              <SizableText color={theme.mainColor.get()} fontWeight="bold">
                Grammar Rules
              </SizableText>
            </Stack>
            <Collapsible collapsed={rulesCollapse}>
              {props.rules.current.map((rule: any, index: number) => (
                <XStack key={index}>
                  <XStack>
                    <Stack pr={1}>
                      <SizableText color={theme.mainColor.get()}>*</SizableText>
                    </Stack>
                    <XStack flexWrap="wrap" width="98%">
                      <SizableText color={theme.mainColor.get()}>
                        {rule.rule}
                      </SizableText>
                    </XStack>
                  </XStack>
                </XStack>
              ))}
            </Collapsible>
          </YStack>
        </XStack>
      </YStack>
    </Pressable>
  );
};

const DetailControls = (props: any) => {
  const [currentVal, setCurrentVal] = useState(0);
  const [playStatus, setPlayStatus] = useState<
    'neutral' | 'playing' | 'paused' | 'stopped'
  >('neutral');

  const stopSound = async () => {
    setCurrentVal(0);
    setPlayStatus('stopped');
  };

  const pauseSound = async () => {
    if (playStatus === 'playing') {
      setPlayStatus('paused');
    }
  };

  const playSound = async () => {
    await setPlayStatus('playing');
    for (let i = currentVal; i < props.perLanguage.silences.length; i++) {
      if (playStatus === 'stopped') {
        break;
      }
      if (playStatus === 'paused') {
        break;
      }
      await wait(props.perLanguage.silences[i]);
      setCurrentVal(i);
    }
    if (playStatus !== 'paused') {
      setCurrentVal(0);
      setPlayStatus('neutral');
    }
  };

  return (
    <Controls
      {...props}
      filename={props.perLanguage.sound}
      playStatus={playStatus}
      playSound={playSound}
      stopSound={stopSound}
      pauseSound={pauseSound}
    />
  );
};

const Detail = (props: any) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const profile = useSelector((state: any) => state.profile);
  const [hasFullAccess, setHasFullAccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const article = props.article;
  const perLanguage = article?.perLanguage[0];
  const sentences = perLanguage?.sentence;

  const previousIndex = useRef<number | null>(null);
  const previousSentenceWordIndex = useRef<number | null>(null);
  const previousSentenceIndex = useRef<number | null>(null);
  const textRefs = useRef<{ current: any[] }>({ current: [] });
  const sentenceRefs = useRef<{ current: any[] }>({ current: [] });
  const selectedSentence = useRef<any>(null);
  const rules = useRef<any[]>([]);

  const {
    userSentences,
    isUserSentencesLoading,
    userSentencesError,
    inactivateSentence,
    saveSentence,
  } = useSentenceOperations();

  const controls = useSelector((state: any) => state.controls);

  useEffect(() => {
    const checkAccess = async () => {
      if (profile?.isStaff) {
        setHasFullAccess(true);
      } else if (perLanguage?.premium === 0) {
        setHasFullAccess(true);
      } else if (perLanguage?.premium === 1 && profile) {
        setHasFullAccess(true);
      } else if (perLanguage?.premium === 2) {
        const accessStatus = await subCheck();
        setHasFullAccess(accessStatus === 'granted');
      }
      setIsLoading(false);
    };

    checkAccess();
  }, [profile, perLanguage]);

  useEffect(() => {
    return () => {
      dispatch(currentCharSave(null));
    };
  }, []);

  const clearSentenceWeb = () => {
    (
      ReactDOM.findDOMNode(
        sentenceRefs.current[previousSentenceIndex.current!]
      ) as HTMLElement
    ).childNodes[0].style['background-color'] = 'transparent';
  };

  const clearSentenceMobile = () => {
    sentenceRefs.current[previousSentenceIndex.current!].setNativeProps({
      style: { backgroundColor: 'transparent' },
    });
  };

  const clearTextWeb = () => {
    (
      ReactDOM.findDOMNode(
        textRefs.current[previousSentenceWordIndex.current!][
          previousIndex.current!
        ]
      ) as HTMLElement
    ).style['background-color'] = 'transparent';
  };

  const clearTextMobile = () => {
    textRefs.current[previousSentenceWordIndex.current!][
      previousIndex.current!
    ]._children[0]._children[0]._children[0].setNativeProps({
      style: { backgroundColor: 'transparent' },
    });
  };

  const clearTextRef = () => {
    if (Platform.OS === 'web') {
      clearTextWeb();
    } else {
      clearTextMobile();
    }
  };

  const clearSentenceRef = () => {
    if (Platform.OS === 'web') {
      clearSentenceWeb();
    } else {
      clearSentenceMobile();
    }
  };

  const handleLoginClick = () => {
    dispatch(toggleLogin(true));
  };

  const handleSubscribeClick = () => {
    dispatch(togglePayment(true));
  };

  const renderPremiumMessage = () => {
    if (!profile) {
      if (perLanguage?.premium === 1) {
        return (
          <Stack>
            <SizableText color={theme.mainColor.get()}>
              Please{' '}
              <SizableText
                color="$pred500"
                onPress={handleLoginClick}
                textDecorationLine="underline"
                cursor="pointer"
                hoverStyle={{ opacity: 0.5 }}
              >
                log in
              </SizableText>{' '}
              to read the full content.
            </SizableText>
          </Stack>
        );
      } else if (perLanguage?.premium === 2) {
        return (
          <Stack>
            <SizableText color={theme.mainColor.get()}>
              Please{' '}
              <SizableText
                color="$pred500"
                onPress={handleLoginClick}
                textDecorationLine="underline"
                cursor="pointer"
                hoverStyle={{ opacity: 0.5 }}
              >
                log in
              </SizableText>{' '}
              to read the full content. After logging in, you can subscribe for
              full access.
            </SizableText>
          </Stack>
        );
      }
    } else if (profile && perLanguage?.premium === 2) {
      return (
        <Stack>
          <SizableText color={theme.mainColor.get()}>
            Please{' '}
            <SizableText
              color="$pred500"
              onPress={handleSubscribeClick}
              textDecorationLine="underline"
              cursor="pointer"
              hoverStyle={{ opacity: 0.5 }}
            >
              subscribe
            </SizableText>{' '}
            to read the full content.
          </SizableText>
        </Stack>
      );
    }
    return null;
  };

  if (isLoading || !article || sentences?.length === 0) {
    return (
      <Stack flexGrow={1} minHeight={600} bg={theme.background}>
        <Loading />
      </Stack>
    );
  }

  const visibleSentences = hasFullAccess ? sentences : [sentences[0]];

  return (
    <Stack>
      {Platform.OS === 'web' && (
        <CharOverlay
          {...props}
          clearTextRef={clearTextRef}
          clearSentenceRef={clearSentenceRef}
          selectedSentence={selectedSentence}
          rules={rules}
        />
      )}
      {Platform.OS !== 'web' && (
        <Stack bg="$gray900" position="absolute" zIndex={900} opacity={0.9}>
          <DetailControls {...props} perLanguage={perLanguage} />
        </Stack>
      )}
      <ScrollView
        pt={!controls && Platform.OS !== 'web' ? 20 : 0}
        bg={theme.background}
      >
        <DetailHeader {...props} article={article} perLanguage={perLanguage} />
        {Platform.OS === 'web' && (
          <>
            <Stack py={20}>
              <LinearGradient
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: 0,
                  height: '100%',
                }}
                colors={['rgba(201,201,201,1)', 'rgba(100,100,100,1)']}
              />
              <ArticleControls article={article} />
            </Stack>
            <Stack bg="black">
              <DetailControls {...props} perLanguage={perLanguage} />
            </Stack>
          </>
        )}
        <Stack pt={20} mx={24} $md={{ mx: 40 }}>
          {article.collections[0]?.articleCount < 2 ? (
            <Breadcrumb
              skipSegments={['article']}
              lastItemLabel={article.title}
            />
          ) : (
            <Breadcrumb
              customPaths={[
                {
                  path: `/collection/${article.collections[0].slug}`,
                  label: article.collections[0].title,
                },
                { path: '', label: article.title },
              ]}
            />
          )}
        </Stack>
        <XStack
          direction="row"
          flexWrap="wrap"
          flexShrink={1}
          display="flex"
          pt={0}
        >
          {visibleSentences.map((sentence: any, sentenceIndex: number) => (
            <DetailBody
              key={sentenceIndex}
              {...props}
              sentenceIndex={sentenceIndex}
              sentences={visibleSentences}
              sentence={sentence}
              article={article}
              textRefs={textRefs}
              sentenceRefs={sentenceRefs}
              previousIndex={previousIndex}
              previousSentenceIndex={previousSentenceIndex}
              previousSentenceWordIndex={previousSentenceWordIndex}
              clearSentenceRef={clearSentenceRef}
              clearTextRef={clearTextRef}
              selectedSentence={selectedSentence}
              rules={rules}
            />
          ))}
        </XStack>
        {!hasFullAccess && (
          <Stack p={20} bg={theme.warningBackground} my={20}>
            {renderPremiumMessage()}
          </Stack>
        )}
        <ReadArticle {...props} article={article} />
        {article.id && hasFullAccess && <Rules article={article} {...props} />}
        <Stack height={50} />
      </ScrollView>
      {Platform.OS !== 'web' && (
        <CharOverlay
          {...props}
          clearTextRef={clearTextRef}
          clearSentenceRef={clearSentenceRef}
          selectedSentence={selectedSentence}
          rules={rules}
        />
      )}
    </Stack>
  );
};

const ReadArticle = (props: { article: { id: string } }) => {
  const profile = useSelector((state: any) => state.profile);
  const dispatch = useDispatch();

  const [createOrUpdateReadStatus, { isLoading: isCreating }] =
    useCreateOrUpdateReadStatusMutation();
  const [deleteReadStatus, { isLoading: isDeleting }] =
    useDeleteReadStatusMutation();

  const {
    data: isReadData,
    isLoading: isLoadingRead,
    error,
    refetch: refetchIsArticleRead,
  } = useIsArticleReadQuery(
    { userId: profile?.id, articleId: props.article.id },
    { skip: !profile?.id }
  );

  const readArticleToggle = async () => {
    if (profile) {
      try {
        if (isReadData) {
          await deleteReadStatus({
            userId: profile.id,
            articleId: props.article.id,
          }).unwrap();
        } else {
          await createOrUpdateReadStatus({
            userId: profile.id,
            articleId: props.article.id,
          }).unwrap();
          rate();
        }
        await refetchIsArticleRead();
      } catch (error) {
        console.error('Error toggling read status:', error);
      }
    } else {
      dispatch(toggleLogin(true));
    }
  };

  const rate = () => {
    if (InAppReview.isAvailable() && getRandomIntInclusive(1, 3) === 3) {
      InAppReview.RequestInAppReview();
    }
  };

  if (!profile?.id || isLoadingRead || error) {
    return null;
  }

  const isRead = isReadData;

  return (
    <Center py={20}>
      <SizableText fontWeight="bold" py={8}>
        Finished reading this article?
      </SizableText>
      <StarButton
        saved={isRead}
        onPress={readArticleToggle}
        disabled={isCreating || isDeleting}
      />
    </Center>
  );
};

const Rules = ({ article }) => {
  const theme = useTheme();
  const [collapsed, setCollapsed] = useState(true);

  const {
    data: rules = [],
    isLoading,
    isError,
  } = useGetRuleMappingsQuery({
    perLanguage: article.perLanguage[0].id,
  });

  const toggleGrammar = () => {
    setCollapsed(!collapsed);
  };

  if (isLoading) {
    return (
      <View>
        <SizableText>Loading...</SizableText>
      </View>
    );
  }

  if (isError) {
    return (
      <View>
        <SizableText>Error loading rules</SizableText>
      </View>
    );
  }

  if (rules.length === 0) {
    return <View />;
  }

  return (
    <Stack mt={40} mb={50}>
      <Center>
        <ColorButton
          title="Grammar Rules"
          onPress={toggleGrammar}
          buttonStyle={{
            width: 200,
            marginTop: 25,
            marginBottom: 25,
          }}
        />
      </Center>
      <Collapsible collapsed={collapsed}>
        {rules.map((rule, index) => (
          <ListCard
            key={index}
            top={{
              style: MainStyles.listCardTop,
            }}
            center={{
              style: [MainStyles.listCardCenter],
            }}
            centerCenter={{
              style: {
                width: '100%',
                alignItems: 'center',
                justifyContent: 'center',
              },
            }}
            bottom={{
              style: MainStyles.listCardBottom,
            }}
            topRightText={{
              style: {
                fontWeight: 'bold',
              },
            }}
            centerCenterText={{
              color: theme.mainColor.get(),
              style: {
                fontWeight: 'bold',
                width: '100%',
              },
            }}
            centerCenterItem={rule.rule}
          />
        ))}
      </Collapsible>
    </Stack>
  );
};

export default Detail;
